import { useFileStore } from "../stores/fileStore";
import { runModelValidationFromCurrentScene } from "../stores/modelValidationStore";
import { usePanelStore } from "../stores/panelStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { logger } from "../utils/logger";
import { resolveAssetStoreFileForPath } from "../utils/environmentZipAssets";
import { extractMjcfOption } from "../utils/mjcfOptions";
import { sanitizeMjcfName } from "../utils/mjcfNames";
import type { ModelValidationSummary } from "../validation/modelValidation";
import type { PhysicsStartOptions } from "./startupPosePolicy";

const OPEN_MODEL_CHECK_PANEL_EVENT = "model-check-open";

if (typeof window !== "undefined") {
  window.addEventListener(OPEN_MODEL_CHECK_PANEL_EVENT, () => {
    usePanelStore.getState().actions.setModelCheckOpen(true);
  });
}

function textToArrayBuffer(text: string): ArrayBuffer {
  const bytes = new globalThis.TextEncoder().encode(text);
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

function findMissingXmlMeshAssets(
  xmlString: string,
  assets: Record<string, ArrayBuffer>
): string[] {
  try {
    const missingMeshes: string[] = [];
    const meshFileMatches = Array.from(xmlString.matchAll(/<mesh\b[^>]*\bfile="([^"]+)"/gi));
    for (const m of meshFileMatches) {
      const path = m[1];
      if (!path) continue;
      const has = hasAssetBufferForXmlPath(path, assets);
      if (!has) missingMeshes.push(path);
    }
    return missingMeshes;
  } catch {
    return [];
  }
}

function showMissingMeshAssetsToast(missingMeshes: string[], loadMode: string): void {
  if (missingMeshes.length === 0 || typeof window === "undefined") return;
  const meshLabel = missingMeshes.length === 1 ? "mesh asset is" : "mesh assets are";
  const modeLabel = loadMode === "minimal" ? "minimal" : "no-mesh";
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: `${missingMeshes.length} physics ${meshLabel} missing; starting in ${modeLabel} mode without mesh collisions.`,
        },
      })
    );
  } catch (error) {
    logger.warn("[physics] Failed to dispatch missing mesh assets toast", error);
  }
}

function showModelValidationToast(summary: ModelValidationSummary): void {
  const actionableFindingCount = summary.errorCount + summary.warningCount;
  if (actionableFindingCount <= 0 || typeof window === "undefined") return;
  const issueLabel = actionableFindingCount === 1 ? "issue" : "issues";
  try {
    window.dispatchEvent(
      new CustomEvent("critter-toast", {
        detail: {
          message: `Check Model found ${actionableFindingCount} static ${issueLabel}; starting physics anyway.`,
          tone: "warning",
          action: {
            label: "Open Check Model",
            event: OPEN_MODEL_CHECK_PANEL_EVENT,
          },
          durationMs: 10000,
        },
      })
    );
  } catch (error) {
    logger.warn("[physics] Failed to dispatch model validation toast", error);
  }
}

function createTerrainMeshObj(
  nrow: number,
  ncol: number,
  values: number[],
  size: number[]
): string {
  const radiusX = Number(size[0]) || 1;
  const radiusY = Number(size[1]) || radiusX;
  const elevation = Number(size[2]) || 0.1;
  const lines: string[] = ["# Generated terrain mesh from inline MJCF hfield"];

  for (let row = 0; row < nrow; row += 1) {
    const v = nrow <= 1 ? 0 : row / (nrow - 1);
    for (let col = 0; col < ncol; col += 1) {
      const u = ncol <= 1 ? 0 : col / (ncol - 1);
      const x = -radiusX + 2 * radiusX * u;
      const y = radiusY - 2 * radiusY * v;
      const z = (Number(values[row * ncol + col]) || 0) * elevation;
      lines.push(`v ${x} ${y} ${z}`);
    }
  }

  for (let row = 0; row < nrow - 1; row += 1) {
    for (let col = 0; col < ncol - 1; col += 1) {
      const a = row * ncol + col + 1;
      const b = a + 1;
      const c = a + ncol;
      const d = c + 1;
      lines.push(`f ${a} ${c} ${b}`);
      lines.push(`f ${b} ${c} ${d}`);
    }
  }

  return `${lines.join("\n")}\n`;
}

function ensureXmlReferencedAssetAliases(
  xmlString: string,
  assets: Record<string, ArrayBuffer>
): string[] {
  if (!xmlString || !assets || typeof assets !== "object") return [];

  const normalize = (value: string) => value.replace(/\\/g, "/").replace(/^\/+/, "");
  const compilerMatch = xmlString.match(/<\s*compiler\b[^>]*>/i);
  const compilerTag = compilerMatch?.[0] || "";
  const getCompilerDir = (attr: "meshdir" | "texturedir", fallback: string) => {
    const match = compilerTag.match(new RegExp(`\\b${attr}\\s*=\\s*"([^"]+)"`, "i"));
    return normalize(match?.[1] || fallback).replace(/\/+$/g, "");
  };
  const meshdir = getCompilerDir("meshdir", "assets");
  const texturedir = getCompilerDir("texturedir", "assets");
  const assetKeys = Object.keys(assets);
  const byBasename = new Map<string, string[]>();
  const byNormalized = new Map<string, string>();

  assetKeys.forEach((key) => {
    const normalizedKey = normalize(key);
    if (normalizedKey && !byNormalized.has(normalizedKey)) byNormalized.set(normalizedKey, key);
    const base = normalizedKey.split("/").pop()?.toLowerCase();
    if (!base) return;
    const list = byBasename.get(base) || [];
    list.push(key);
    byBasename.set(base, list);
  });

  const created: string[] = [];
  const refs = Array.from(
    xmlString.matchAll(/<\s*(mesh|texture|hfield)\b[^>]*\bfile\s*=\s*"([^"]+)"/gi)
  );

  refs.forEach((match) => {
    const kind = String(match[1] || "").toLowerCase();
    const raw = String(match[2] || "").trim();
    if (!raw || /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) return;

    const normalized = normalize(raw);
    if (!normalized) return;

    const compilerDir = kind === "texture" ? texturedir : meshdir;
    const resolvedPath =
      compilerDir && normalized !== compilerDir && !normalized.startsWith(`${compilerDir}/`)
        ? `${compilerDir}/${normalized}`
        : normalized;
    if (assets[normalized] && assets[resolvedPath]) return;

    let sourceKey = byNormalized.get(normalized) || byNormalized.get(resolvedPath) || "";
    if (!sourceKey) {
      const basename = normalized.split("/").pop()?.toLowerCase();
      const candidates = basename ? byBasename.get(basename) || [] : [];
      const uniqueCandidates = Array.from(new Set(candidates));
      if (uniqueCandidates.length !== 1) return;
      sourceKey = uniqueCandidates[0];
    }

    for (const alias of [normalized, resolvedPath]) {
      if (!alias || assets[alias]) continue;
      assets[alias] = assets[sourceKey];
      created.push(alias);
      byNormalized.set(alias, alias);
    }
  });

  return created;
}

function hasAssetBufferForXmlPath(path: string, assets: Record<string, ArrayBuffer>): boolean {
  const normalizedPath = String(path || "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  if (!normalizedPath) return false;
  const keys = Object.keys(assets);
  return keys.some((key) => {
    const normalizedKey = String(key || "")
      .replace(/\\/g, "/")
      .replace(/^\/+/, "");
    return (
      normalizedKey === normalizedPath ||
      normalizedKey === `assets/${normalizedPath}` ||
      normalizedKey.endsWith(`/${normalizedPath}`)
    );
  });
}

async function addFileMapAssetBuffers(
  fileMap: Record<string, unknown>,
  assets: Record<string, ArrayBuffer>
): Promise<void> {
  const rawEntries = Object.entries(fileMap || {}) as Array<[string, unknown]>;
  const entries = rawEntries.map((pair) => [pair[0], pair[1] as File]) as Array<[string, File]>;
  for (const [name, file] of entries) {
    try {
      if (name.toLowerCase().endsWith(".xml")) continue;
      const buf = await file.arrayBuffer();
      assets[name] = buf;
    } catch (error) {
      logger.warn(`[physics] Failed to load asset buffer for ${name}`, error);
    }
  }
}

async function addRegisteredMeshAssetBuffers(
  meshPaths: string[],
  assets: Record<string, ArrayBuffer>
): Promise<string[]> {
  const resolved: string[] = [];
  for (const meshPath of meshPaths) {
    if (!meshPath || hasAssetBufferForXmlPath(meshPath, assets)) continue;
    try {
      const file = await resolveAssetStoreFileForPath(meshPath, meshPath);
      if (!file) continue;
      assets[meshPath] = await file.arrayBuffer();
      resolved.push(meshPath);
    } catch (error) {
      logger.warn(`[physics] Failed to load registered mesh asset for ${meshPath}`, error);
    }
  }
  return resolved;
}

function prepareInlineHFieldsForMujocoWasm(
  xmlString: string,
  assets: Record<string, ArrayBuffer>
): string {
  if (!xmlString.includes("<hfield") || !xmlString.includes("elevation=")) return xmlString;
  if (typeof DOMParser === "undefined" || typeof XMLSerializer === "undefined") return xmlString;

  const doc = new DOMParser().parseFromString(xmlString, "application/xml");
  if (doc.querySelector("parsererror")) return xmlString;

  let generatedCount = 0;
  Array.from(doc.querySelectorAll("asset > hfield")).forEach((hfield, index) => {
    if (hfield.getAttribute("file")) return;
    const elevation = hfield.getAttribute("elevation");
    if (!elevation) return;

    const nrow = Number(hfield.getAttribute("nrow"));
    const ncol = Number(hfield.getAttribute("ncol"));
    const values = elevation
      .trim()
      .split(/\s+/)
      .map(Number)
      .filter((value) => Number.isFinite(value));
    if (!Number.isInteger(nrow) || !Number.isInteger(ncol) || nrow <= 0 || ncol <= 0) return;
    if (values.length !== nrow * ncol) return;

    const name = sanitizeMjcfName(hfield.getAttribute("name") || `hfield_${index}`);
    const meshName = sanitizeMjcfName(`${name}_mesh`);
    let assetPath = `assets/generated_terrain_meshes/${name}.obj`;
    let meshFile = `generated_terrain_meshes/${name}.obj`;
    let suffix = 1;
    while (assets[assetPath]) {
      assetPath = `assets/generated_terrain_meshes/${name}_${suffix}.obj`;
      meshFile = `generated_terrain_meshes/${name}_${suffix}.obj`;
      suffix += 1;
    }

    const size = (hfield.getAttribute("size") || "")
      .trim()
      .split(/\s+/)
      .map(Number)
      .filter((value) => Number.isFinite(value));
    assets[assetPath] = textToArrayBuffer(createTerrainMeshObj(nrow, ncol, values, size));

    const mesh = doc.createElement("mesh");
    mesh.setAttribute("name", meshName);
    mesh.setAttribute("file", meshFile);
    hfield.replaceWith(mesh);

    Array.from(doc.querySelectorAll("geom")).forEach((geom) => {
      if (geom.getAttribute("type") !== "hfield") return;
      if (geom.getAttribute("hfield") !== name) return;
      geom.setAttribute("type", "mesh");
      geom.setAttribute("mesh", meshName);
      geom.removeAttribute("hfield");
    });

    generatedCount += 1;
  });

  if (generatedCount === 0) return xmlString;
  return new XMLSerializer().serializeToString(doc);
}

export interface StartSimulationFromCurrentFilesOptions extends Pick<
  PhysicsStartOptions,
  "preserveActuatorControls" | "startupPosePolicy"
> {
  /** When set, XML keyframe uses fileStore keyframes["init"] for robot; object bodies use current scene. Used by warmstart so first frame matches design pose. */
  keyframeSource?: "currentScene" | "initKeyframe";
  /** Body UUIDs to treat as objects (keyframe from scene). Only used when keyframeSource === "initKeyframe". */
  objectBodyUuids?: string[];
  /** When set, keyframe is built from this scene snapshot instead of getSceneState(). Ensures worker seed matches the intended pose (avoids race with IK/store). */
  overrideSceneStateForKeyframe?: Record<string, unknown>;
  /** When set, use this XML instead of generating (e.g. warmstart supplies the same XML for the zip so playback joint order matches). */
  preGeneratedXml?: string;
}

type PhysicsWorkerInputDiagnostic = {
  source: string;
  stage: string;
  level: "info" | "warn" | "error";
  message: string;
  data?: unknown;
};

type PreparePhysicsWorkerInputHooks = {
  addDiagnostic?: (entry: PhysicsWorkerInputDiagnostic) => void;
  setLoadMode?: (loadMode: "full" | "noInertials" | "minimal" | "noMeshes") => void;
  setTimestep?: (timestep: number) => void;
  setSolverIterations?: (iterations: number) => void;
  setSolverTolerance?: (tolerance: number) => void;
  showMissingMeshesToast?: boolean;
};

export async function prepareCurrentPhysicsWorkerInput(
  options?: StartSimulationFromCurrentFilesOptions,
  hooks: PreparePhysicsWorkerInputHooks = {}
) {
  const fileStoreState = useFileStore.getState();
  const fileActions = fileStoreState.actions;
  let selectedLoadMode = usePhysicsStore.getState().loadMode || "full";

  let xmlString: string | null = options?.preGeneratedXml ?? null;
  if (!xmlString) {
    try {
      xmlString = await fileActions.generateXmlFromState({
        startupPosePolicy: options?.startupPosePolicy || "currentScenePose",
        xmlNamingMode: "uuid",
        ...(options?.keyframeSource != null && { keyframeSource: options.keyframeSource }),
        ...(options?.objectBodyUuids != null && { objectBodyUuids: options.objectBodyUuids }),
        ...(options?.overrideSceneStateForKeyframe != null && {
          overrideSceneStateForKeyframe: options.overrideSceneStateForKeyframe,
        }),
      });
      logger.info("[physics] using xml:", "generated (forced)");
      logger.debug("[physics] XML length:", xmlString?.length);
      logger.debug(
        "[physics] XML preview:",
        xmlString?.substring(0, 1000) + (xmlString && xmlString.length > 1000 ? "..." : "")
      );
    } catch (e) {
      logger.warn("Failed to generate XML:", e);
    }
  } else {
    logger.info("[physics] using xml:", "pre-generated");
  }
  if (!xmlString) throw new Error("No XML available to start simulation.");

  try {
    const xmlOption = extractMjcfOption(xmlString);
    const timestepMatch = xmlString.match(/<\s*option\b[^>]*\btimestep\s*=\s*"([^"]+)"/i);
    if (xmlOption || (timestepMatch && timestepMatch[1])) {
      const xmlTimestep = xmlOption?.timestep ?? Number(timestepMatch?.[1]);
      if (Number.isFinite(xmlTimestep) && xmlTimestep > 0) {
        logger.info(`[physics] Extracted timestep from XML: ${xmlTimestep}`);
        hooks.setTimestep?.(xmlTimestep);
        if (xmlOption) {
          hooks.setSolverIterations?.(xmlOption.iterations);
          hooks.setSolverTolerance?.(xmlOption.tolerance);
        }
      } else {
        logger.warn(`[physics] Invalid timestep in XML: ${timestepMatch?.[1]}, using default`);
      }
    } else {
      logger.debug("[physics] No timestep found in XML, using physics store default");
    }
  } catch (e) {
    logger.warn("[physics] Failed to extract timestep from XML:", e);
  }

  const assets: Record<string, ArrayBuffer> = {};
  await addFileMapAssetBuffers(fileStoreState.fileMap || {}, assets);
  xmlString = prepareInlineHFieldsForMujocoWasm(xmlString, assets);
  const aliasedAssets = ensureXmlReferencedAssetAliases(xmlString, assets);
  if (aliasedAssets.length > 0) {
    hooks.addDiagnostic?.({
      source: "bridge",
      stage: "preflight",
      level: "info",
      message: "Aliased mesh assets for worker paths",
      data: { aliases: aliasedAssets },
    });
  }

  let missingMeshes = findMissingXmlMeshAssets(xmlString, assets);
  const registeredMeshAssets = await addRegisteredMeshAssetBuffers(missingMeshes, assets);
  if (registeredMeshAssets.length > 0) {
    const registeredAliases = ensureXmlReferencedAssetAliases(xmlString, assets);
    hooks.addDiagnostic?.({
      source: "bridge",
      stage: "preflight",
      level: "info",
      message: "Loaded registered mesh assets for worker paths",
      data: { assets: registeredMeshAssets, aliases: registeredAliases },
    });
    missingMeshes = findMissingXmlMeshAssets(xmlString, assets);
  }

  if (missingMeshes.length) {
    const desiredMode = selectedLoadMode === "minimal" ? "minimal" : "noMeshes";
    if (desiredMode !== selectedLoadMode) {
      hooks.setLoadMode?.(desiredMode);
      selectedLoadMode = desiredMode;
    }
    hooks.addDiagnostic?.({
      source: "bridge",
      stage: "preflight",
      level: "warn",
      message: "Mesh assets missing; switching load mode",
      data: { missingMeshes },
    });
    if (hooks.showMissingMeshesToast) showMissingMeshAssetsToast(missingMeshes, selectedLoadMode);
  } else {
    hooks.addDiagnostic?.({
      source: "bridge",
      stage: "preflight",
      level: "info",
      message: "Mesh assets verified",
      data: { assetCount: Object.keys(assets).length },
    });
  }

  const bodyNamesHint: string[] = [];
  try {
    const matches = Array.from(xmlString.matchAll(/<\s*body\b[^>]*\bname\s*=\s*"([^"]+)"/gi));
    const seen = new Set<string>();
    matches.forEach((m) => {
      const raw = m?.[1];
      const n = raw ? sanitizeMjcfName(raw) : "";
      if (!n || seen.has(n) || n === "world") return;
      seen.add(n);
      bodyNamesHint.push(n);
    });
  } catch (error) {
    logger.warn("[physics] Failed to build bodyNamesHint from scene", error);
  }

  return {
    xmlString,
    assets,
    bodyNamesHint,
    loadMode: selectedLoadMode,
    missingMeshes,
  };
}

export async function startSimulationFromCurrentFiles(
  options?: StartSimulationFromCurrentFilesOptions
) {
  const physicsStoreState = usePhysicsStore.getState();
  const physicsActions = physicsStoreState.actions;
  const validationResult = runModelValidationFromCurrentScene();
  if (validationResult?.summary.findingCount) {
    const { summary } = validationResult;
    physicsActions.addDiagnostic({
      source: "model-validation",
      stage: "preflight",
      level: summary.errorCount > 0 || summary.warningCount > 0 ? "warn" : "info",
      message: "Static model validation found issues before physics start",
      data: summary,
    });
    showModelValidationToast(summary);
  }

  const { xmlString, assets, bodyNamesHint, loadMode } = await prepareCurrentPhysicsWorkerInput(
    options,
    {
      addDiagnostic: physicsActions.addDiagnostic,
      setLoadMode: physicsActions.setLoadMode,
      setTimestep: physicsActions.setTimestep,
      setSolverIterations: physicsActions.setSolverIterations,
      setSolverTolerance: physicsActions.setSolverTolerance,
      showMissingMeshesToast: true,
    }
  );

  logger.debug("[physics] starting with assets:", Object.keys(assets));
  physicsActions.addDiagnostic({
    source: "bridge",
    stage: "start",
    level: "info",
    message: "Starting MuJoCo worker",
    data: { loadMode, assetCount: Object.keys(assets).length },
  });

  await physicsActions.startFromXml(xmlString, assets, bodyNamesHint, {
    ...options,
    startupPosePolicy: options?.startupPosePolicy || "currentScenePose",
  });
}

/**
 * Start physics using the currently loaded XML file verbatim (no regeneration).
 * This is important for exact env-bundle reproduction (training playback).
 */
export async function startSimulationFromXmlFile(
  options?: Pick<PhysicsStartOptions, "preserveActuatorControls"> & { xmlFile?: File | null }
) {
  const fileStoreState = useFileStore.getState();
  const physicsStoreState = usePhysicsStore.getState();
  const physicsActions = physicsStoreState.actions;
  let selectedLoadMode = physicsStoreState.loadMode || "full";

  const xmlFile = options?.xmlFile ?? fileStoreState.xmlFile;
  if (!xmlFile) throw new Error("No XML file loaded (xmlFile is null).");

  let xmlString: string | null = null;
  try {
    xmlString = await xmlFile.text();
    logger.info("[physics] using xml:", "xmlFile (verbatim)");
    logger.debug("[physics] XML length:", xmlString?.length);
  } catch (e) {
    logger.warn("[physics] Failed to read xmlFile:", e);
  }
  if (!xmlString) throw new Error("No XML available to start simulation (xmlFile read failed).");

  // Extract timestep from XML to ensure worker loop matches MuJoCo's internal timestep
  try {
    const xmlOption = extractMjcfOption(xmlString);
    const timestepMatch = xmlString.match(/<\s*option\b[^>]*\btimestep\s*=\s*"([^"]+)"/i);
    if (xmlOption || (timestepMatch && timestepMatch[1])) {
      const xmlTimestep = xmlOption?.timestep ?? Number(timestepMatch?.[1]);
      if (Number.isFinite(xmlTimestep) && xmlTimestep > 0) {
        physicsActions.setTimestep(xmlTimestep);
        if (xmlOption) {
          physicsActions.setSolverIterations(xmlOption.iterations);
          physicsActions.setSolverTolerance(xmlOption.tolerance);
        }
      }
    }
  } catch (e) {
    logger.warn("[physics] Failed to extract timestep from xmlFile:", e);
  }

  const assets: Record<string, ArrayBuffer> = {};
  await addFileMapAssetBuffers(fileStoreState.fileMap || {}, assets);
  xmlString = prepareInlineHFieldsForMujocoWasm(xmlString, assets);
  const aliasedAssets = ensureXmlReferencedAssetAliases(xmlString, assets);
  if (aliasedAssets.length > 0) {
    physicsActions.addDiagnostic({
      source: "bridge",
      stage: "preflight",
      level: "info",
      message: "Aliased mesh assets for worker paths",
      data: { aliases: aliasedAssets },
    });
  }

  // Preflight: ensure meshes referenced in XML are present in assets
  let missingMeshes = findMissingXmlMeshAssets(xmlString, assets);
  const registeredMeshAssets = await addRegisteredMeshAssetBuffers(missingMeshes, assets);
  if (registeredMeshAssets.length > 0) {
    const registeredAliases = ensureXmlReferencedAssetAliases(xmlString, assets);
    physicsActions.addDiagnostic({
      source: "bridge",
      stage: "preflight",
      level: "info",
      message: "Loaded registered mesh assets for worker paths",
      data: { assets: registeredMeshAssets, aliases: registeredAliases },
    });
    missingMeshes = findMissingXmlMeshAssets(xmlString, assets);
  }

  if (missingMeshes.length) {
    const desiredMode = selectedLoadMode === "minimal" ? "minimal" : "noMeshes";
    if (desiredMode !== selectedLoadMode) {
      physicsActions.setLoadMode(desiredMode);
      selectedLoadMode = desiredMode;
    }
    physicsActions.addDiagnostic({
      source: "bridge",
      stage: "preflight",
      level: "warn",
      message: "Mesh assets missing; switching load mode",
      data: { missingMeshes },
    });
    showMissingMeshAssetsToast(missingMeshes, selectedLoadMode);
  }

  // Build bodyNamesHint by parsing the XML itself (most reliable: matches MuJoCo model body names).
  const bodyNamesHint: string[] = [];
  try {
    const matches = Array.from(xmlString.matchAll(/<\s*body\b[^>]*\bname\s*=\s*"([^"]+)"/gi));
    const seen = new Set<string>();
    matches.forEach((m) => {
      const raw = m?.[1];
      const n = raw ? sanitizeMjcfName(raw) : "";
      if (!n || seen.has(n) || n === "world") return;
      seen.add(n);
      bodyNamesHint.push(n);
    });
  } catch (error) {
    logger.warn("[physics] Failed to build bodyNamesHint from xmlFile", error);
  }

  physicsActions.addDiagnostic({
    source: "bridge",
    stage: "start",
    level: "info",
    message: "Starting MuJoCo worker (verbatim xmlFile)",
    data: { loadMode: selectedLoadMode, assetCount: Object.keys(assets).length },
  });

  await physicsActions.startFromXml(xmlString, assets, bodyNamesHint, {
    ...options,
    startupPosePolicy: "verbatimXmlPose",
  });
}

export async function exportGeneratedXml(filename = "physics_generated.xml") {
  const fileStoreState = useFileStore.getState();
  const fileActions = fileStoreState.actions;
  try {
    const xml = await fileActions.generateXmlFromState({
      startupPosePolicy: "currentScenePose",
    });
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    logger.info("[physics] exported generated XML (len=", xml.length, ")");
  } catch (e) {
    logger.error("[physics] failed to export generated XML:", e);
  }
}

export async function stopSimulation(options?: { restoreScene?: boolean }) {
  await usePhysicsStore.getState().actions.stop(options);
}
